import React from "react";

/**
 * A beautiful, minimalist continuous-line drawing of a majestic horse head.
 * Perfect as a large, faint background watermark or decorative element.
 */
export function HorseLineArt({ className = "text-stone-300/60", ...props }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      fill="none"
      stroke="currentColor"
      strokeWidth="0.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      {/* Continuous line art of a horse head */}
      <path d="M15,85 C25,80 32,70 35,55 C37,45 32,35 28,25 C26,20 28,15 32,12 C35,10 40,12 42,16 C45,22 43,30 45,35 C47,40 52,42 58,40 C65,38 72,32 78,25 C82,20 85,22 83,28 C80,35 72,45 65,52 C58,58 48,62 42,68 C36,74 32,82 30,90" />
      <path d="M42,16 C45,10 50,5 55,8 C58,10 56,15 53,18 C50,21 45,22 42,16 Z" />
      {/* Mane details */}
      <path d="M32,25 C36,22 40,24 41,28" />
      <path d="M35,35 C40,32 44,34 45,38" />
      <path d="M38,45 C43,42 47,44 48,48" />
      <path d="M40,55 C46,52 51,54 52,59" />
      {/* Eye */}
      <circle cx="50" cy="30" r="0.75" fill="currentColor" />
      {/* Nostril */}
      <path d="M75,32 C74,33 73,33 72,32" strokeWidth="0.75" />
    </svg>
  );
}

/**
 * A subtle architectural draft grid overlay with coordinate markings.
 * Gives a "draft/layout" ("udkast") feel to any container.
 */
export function DraftGrid({ className = "opacity-[0.12] text-stone-900", ...props }) {
  return (
    <div className={`absolute inset-0 pointer-events-none select-none ${className}`} {...props}>
      {/* Grid lines using the site's warm gray border color */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#E2DDD5_1.5px,transparent_1.5px),linear-gradient(to_bottom,#E2DDD5_1.5px,transparent_1.5px)] bg-[size:50px_50px]" />
      
      {/* Coordinate tick marks */}
      <div className="absolute top-2 left-2 font-mono text-[8px] tracking-widest uppercase">SEC_01 // REF_473</div>
      <div className="absolute top-2 right-2 font-mono text-[8px] tracking-widest uppercase">X: 104.2 Y: 88.5</div>
      <div className="absolute bottom-2 left-2 font-mono text-[8px] tracking-widest uppercase">SCALE 1:100</div>
      <div className="absolute bottom-2 right-2 font-mono text-[8px] tracking-widest">© EQUINESPORT</div>
      
      {/* Crosshairs */}
      <div className="absolute top-1/2 left-4 w-4 h-[1px] bg-current opacity-30" />
      <div className="absolute top-1/2 right-4 w-4 h-[1px] bg-current opacity-30" />
      <div className="absolute left-1/2 top-4 h-4 w-[1px] bg-current opacity-30" />
      <div className="absolute left-1/2 bottom-4 h-4 w-[1px] bg-current opacity-30" />
    </div>
  );
}

/**
 * Elegant editorial corner brackets to frame a card or section.
 */
export function CornerBrackets({ className = "text-stone-400", size = "w-4 h-4", ...props }) {
  return (
    <div className={`absolute inset-0 pointer-events-none z-20 ${className}`} {...props}>
      {/* Top Left */}
      <div className={`absolute top-0 left-0 border-t-2 border-l-2 border-solid border-current ${size}`} />
      {/* Top Right */}
      <div className={`absolute top-0 right-0 border-t-2 border-r-2 border-solid border-current ${size}`} />
      {/* Bottom Left */}
      <div className={`absolute bottom-0 left-0 border-b-2 border-l-2 border-solid border-current ${size}`} />
      {/* Bottom Right */}
      <div className={`absolute bottom-0 right-0 border-b-2 border-r-2 border-solid border-current ${size}`} />
    </div>
  );
}

/**
 * Flowing, abstract vector lines representing movement, wind, or dressage grace.
 */
export function OrganicLines({ className = "text-stone-300/50", ...props }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 100"
      fill="none"
      stroke="currentColor"
      strokeWidth="0.35"
      className={className}
      {...props}
    >
      <path d="M-20,50 C30,20 70,80 120,40 C170,0 210,60 250,30" />
      <path d="M-20,55 C35,28 75,85 125,43 C175,1 215,63 255,33" />
      <path d="M-20,45 C25,12 65,75 115,37 C165,-1 205,57 245,27" />
    </svg>
  );
}

/**
 * Premium equestrian crest/seal.
 */
export function EquestrianEmblem({ className = "text-stone-800", ...props }) {
  return (
    <div className={`flex flex-col items-center justify-center text-center p-4 select-none ${className}`} {...props}>
      <img
        src="https://assets.auxodev.dk/assets/47342516-e670-4852-ad73-fdec3014e752/3a775662-e642-401a-9c70-e44737456407.png"
        alt="Equinesport Logo"
        className="w-16 h-16 mb-2 object-contain"
      />
      <span className="font-display text-[10px] tracking-[0.3em] uppercase font-semibold">EST. 2012</span>
      <span className="text-[7px] tracking-[0.4em] uppercase text-stone-400 font-medium">HEINEKING</span>
    </div>
  );
}

/**
 * Elegant horizontal section divider with a tiny diamond or horse emblem.
 */
export function DecorativeDivider({ className = "my-12", ...props }) {
  return (
    <div className={`flex items-center justify-center gap-4 ${className}`} {...props}>
      <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-stone-300" />
      <div className="flex items-center gap-1 text-stone-400">
        <div className="w-1.5 h-1.5 rotate-45 bg-stone-300" />
        <img
          src="https://assets.auxodev.dk/assets/47342516-e670-4852-ad73-fdec3014e752/161a079b-25e4-4ffe-88ad-7f5a02a4ef20.png"
          alt="Equinesport Logo Icon"
          className="w-6 h-6 object-contain"
        />
        <div className="w-1.5 h-1.5 rotate-45 bg-stone-300" />
      </div>
      <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-stone-300" />
    </div>
  );
}
